import {
  X,
  Calendar,
  FileText,
  FolderKanban,
  Handshake,
  CheckCircle2,
  Archive,
  Bell,
  AlertCircle,
  Users,
  Link2,
  Tag,
  Paperclip,
  ExternalLink,
} from "lucide-react";
import { cn } from "@/utils/Functions";
import { Button } from "@/components/ui/Button";
import { Avatar } from "@/components/ui/Avatar";
import type { InboxNotification } from "@/types/inbox.types";
import {
  getTypeIcon,
  getTypeColor,
  getActorAvatar,
  getActorInitials,
} from "../lib/utils";
import { useActorAvatarLookup } from "@/hooks/inbox/useActorAvatarLookup";

interface NotificationDetailProps {
  notification: InboxNotification;
  onClose: () => void;
  onMarkRead: (id: string) => void;
  onArchive: (id: string) => void;
}

export function NotificationDetail({
  notification,
  onClose,
  onMarkRead,
  onArchive,
}: NotificationDetailProps) {
  const avatarLookup = useActorAvatarLookup();
  const Icon = getTypeIcon(notification.type);
  const typeColor = getTypeColor(notification.type);
  const isUnread = notification.status === "unread";
  const priorityColors = {
    high: "text-destructive",
    medium: "text-yellow-600 dark:text-yellow-500",
    low: "text-muted-foreground",
  };

  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp);
    return new Intl.DateTimeFormat("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(date);
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden w-full">
      <header className="shrink-0 px-6 py-4 border-b border-border/50 bg-background">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className={cn("shrink-0 p-2.5 rounded-lg", typeColor)}>
              <Icon className="size-5" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-semibold text-foreground mb-1 wrap-break-word">
                {notification.title}
              </h2>
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className={cn(
                    "text-xs font-medium px-2 py-0.5 rounded-md",
                    priorityColors[notification.priority],
                    "bg-muted"
                  )}
                >
                  {notification.priority} priority
                </span>
                {notification.actionRequired && (
                  <span className="text-xs font-medium px-2 py-0.5 rounded-md text-destructive bg-destructive/10 flex items-center gap-1">
                    <AlertCircle className="size-3" />
                    Action required
                  </span>
                )}
                {isUnread && (
                  <span className="text-xs font-medium px-2 py-0.5 rounded-md text-primary bg-primary/10">
                    Unread
                  </span>
                )}
              </div>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="shrink-0"
            onClick={onClose}
            aria-label="Close detail view"
          >
            <X className="size-4" />
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => onMarkRead(notification.id)}>
            {isUnread ? (
              <>
                <CheckCircle2 className="size-4 mr-1.5" />
                Mark as read
              </>
            ) : (
              <>
                <Bell className="size-4 mr-1.5" />
                Mark as unread
              </>
            )}
          </Button>
          <Button variant="outline" size="sm" onClick={() => onArchive(notification.id)}>
            <Archive className="size-4 mr-1.5" />
            Archive
          </Button>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto px-6 py-6">
        <div className="space-y-6">
          <section>
            <h3 className="text-sm font-semibold text-foreground mb-2">Description</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">{notification.description}</p>
          </section>

          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground mb-3">Details</h3>
            <div className="space-y-2.5">
              <div className="flex items-start gap-3">
                <FolderKanban className="size-4 text-muted-foreground mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground mb-0.5">Entity</p>
                  <p className="text-sm font-medium text-foreground">
                    {notification.metadata.entityName}
                  </p>
                </div>
              </div>

              {notification.metadata.actor && (
                <div className="flex items-start gap-3">
                  <Avatar
                    src={getActorAvatar(notification.metadata.actor, avatarLookup)}
                    fallback={getActorInitials(notification.metadata.actor)}
                    className="size-8 shrink-0"
                    alt={notification.metadata.actor}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-muted-foreground mb-0.5">Actor</p>
                    <p className="text-sm font-medium text-foreground">{notification.metadata.actor}</p>
                  </div>
                </div>
              )}

              <div className="flex items-start gap-3">
                <Calendar className="size-4 text-muted-foreground mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground mb-0.5">Time</p>
                  <p className="text-sm font-medium text-foreground">
                    {formatDate(notification.timestamp)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">{notification.relativeTime}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <FileText className="size-4 text-muted-foreground mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground mb-0.5">Type</p>
                  <p className="text-sm font-medium text-foreground capitalize">{notification.type}</p>
                </div>
              </div>
            </div>
          </section>

          {notification.metadata.participants && notification.metadata.participants.length > 0 && (
            <section>
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Users className="size-4" />
                Participants ({notification.metadata.participants.length})
              </h3>
              <div className="flex flex-wrap gap-2">
                {notification.metadata.participants.map((participant) => (
                  <div
                    key={participant}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted/50 border border-border/50"
                  >
                    <Avatar
                      src={getActorAvatar(participant, avatarLookup)}
                      fallback={getActorInitials(participant)}
                      className="size-6"
                      alt={participant}
                    />
                    <span className="text-sm font-medium text-foreground">{participant}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {notification.metadata.relatedItems && notification.metadata.relatedItems.length > 0 && (
            <section>
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Link2 className="size-4" />
                Related Items
              </h3>
              <div className="space-y-2">
                {notification.metadata.relatedItems.map((item) => {
                  const ItemIcon =
                    item.type === "contract"
                      ? Handshake
                      : item.type === "deal"
                        ? FolderKanban
                        : FileText;
                  return (
                    <div
                      key={item.id}
                      className="flex items-center gap-3 px-3 py-2 rounded-lg bg-muted/30 border border-border/50 hover:bg-muted/50 transition-colors cursor-pointer group"
                    >
                      <div className="p-1.5 rounded-md bg-muted">
                        <ItemIcon className="size-4 text-muted-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground">{item.name}</p>
                        <p className="text-xs text-muted-foreground capitalize">{item.type}</p>
                      </div>
                      <ExternalLink className="size-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {notification.metadata.tags && notification.metadata.tags.length > 0 && (
            <section>
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Tag className="size-4" />
                Tags
              </h3>
              <div className="flex flex-wrap gap-2">
                {notification.metadata.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-1 text-xs font-medium rounded-md bg-primary/10 text-primary border border-primary/20"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </section>
          )}

          {notification.metadata.attachments && notification.metadata.attachments.length > 0 && (
            <section>
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Paperclip className="size-4" />
                Attachments ({notification.metadata.attachments.length})
              </h3>
              <div className="space-y-2">
                {notification.metadata.attachments.map((attachment, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg bg-muted/30 border border-border/50 hover:bg-muted/50 transition-colors cursor-pointer group"
                  >
                    <div className="p-1.5 rounded-md bg-muted">
                      <FileText className="size-4 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{attachment.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {attachment.size} • {attachment.type.toUpperCase()}
                      </p>
                    </div>
                    <ExternalLink className="size-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                ))}
              </div>
            </section>
          )}

          {notification.actionRequired && (
            <section className="pt-4 border-t border-border/50">
              <div className="p-4 rounded-lg bg-destructive/5 border border-destructive/20">
                <div className="flex items-start gap-3">
                  <AlertCircle className="size-5 text-destructive shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-destructive mb-1">Action Required</h4>
                    <p className="text-sm text-muted-foreground">
                      This notification requires your attention. Please review and take the necessary
                      action.
                    </p>
                  </div>
                </div>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
